import { AxiosError } from 'axios'
import { store } from '../../../app/store'
import APIClient from '../../../apiClient'
import StatusE from '../../../types/statusE'
import { ErrorT } from '../../../types/errorT'
import {
  CreateNotificationSubscriptionT,
  UpdateNotificationPreferencesT,
  VapidKeyResponseT,
  NotificationSubscriptionT,
} from '../../../types/notificationSubscriptionT'
import {
  setStatus,
  setError,
  setSubscriptions,
  setVapidKey,
  setLoadingVapidKey,
  setLoadingSubscriptions,
  setSubscriptionCreated,
  setSubscriptionUpdated,
} from './slice'

const apiClient = new APIClient()
const apiInstance = apiClient.getInstance()

const publicApiClient = new APIClient(false)
const publicApiInstance = publicApiClient.getInstance()

export const getVapidKey = () => (dispatch: typeof store.dispatch) => {
  dispatch(setLoadingVapidKey(true))

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Request timed out. Please check your internet connection and try again.')), 10000)
  )

  const apiCall = publicApiInstance.get<VapidKeyResponseT>('/notification/vapid-key')

  Promise.race([apiCall, timeoutPromise])
    .then((response) => {
      if (response.data?.data?.public_key) {
        dispatch(setVapidKey(response.data.data.public_key))
      }
    })
    .catch((error: ErrorT | AxiosError | Error) => {
      if (error instanceof AxiosError) {
        const { status, name, message } = error
        dispatch(setError({ status, name, message }))
      } else if (error instanceof Error) {
        dispatch(setError({
          status: 408,
          name: 'TimeoutError',
          message: error.message
        }))
      } else {
        dispatch(setError(error))
      }
    })
}

export const getUserSubscriptions = () => (dispatch: typeof store.dispatch) => {
  dispatch(setLoadingSubscriptions(true))

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Request timed out. Please check your internet connection and try again.')), 10000)
  )

  const apiCall = apiInstance.get<{ data: NotificationSubscriptionT[] }>('/notification/subscription')

  Promise.race([apiCall, timeoutPromise])
    .then((response) => {
      if (response.data?.data) {
        const subscriptions = Array.isArray(response.data.data) ? response.data.data : [response.data.data]
        dispatch(setSubscriptions(subscriptions))
      } else {
        dispatch(setSubscriptions([]))
      }
    })
    .catch((error: ErrorT | AxiosError | Error) => {
      if (error instanceof AxiosError) {
        const { status, name, message } = error
        dispatch(setError({ status, name, message }))
      } else if (error instanceof Error) {
        dispatch(setError({
          status: 408,
          name: 'TimeoutError',
          message: error.message
        }))
      } else {
        dispatch(setError(error))
      }
    })
}

export const createSubscription =
  (subscription: CreateNotificationSubscriptionT) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(setStatus(StatusE.LOADING))

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out. Please check your internet connection and try again.')), 30000)
    )

    const apiCall = apiInstance
      .post<{ data: NotificationSubscriptionT }>('/notification/subscription', subscription, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

    Promise.race([apiCall, timeoutPromise])
      .then((response) => {
        if (response.data?.data) {
          dispatch(setSubscriptionCreated(response.data.data))
          // Refresh subscriptions list
          dispatch(getUserSubscriptions())
        }
      })
      .catch((error: ErrorT | AxiosError | Error) => {
        if (error instanceof AxiosError) {
          const { status, name, message } = error
          dispatch(setError({ status, name, message }))
        } else if (error instanceof Error) {
          dispatch(setError({
            status: 408,
            name: 'TimeoutError',
            message: error.message
          }))
        } else {
          dispatch(setError(error))
        }
      })
  }

export const updateSubscriptionPreferences =
  (subscriptionId: string, preferences: UpdateNotificationPreferencesT) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(setStatus(StatusE.LOADING))

    apiInstance
      .put<{ data: NotificationSubscriptionT }>(`/notification/subscription/${subscriptionId}`, preferences, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        if (response.data?.data) {
          dispatch(setSubscriptionUpdated(response.data.data))
          // Refresh subscriptions list
          dispatch(getUserSubscriptions())
        }
      })
      .catch((error: ErrorT | AxiosError) => {
        if (error instanceof AxiosError) {
          const { status, name, message } = error
          dispatch(setError({ status, name, message }))
        } else {
          dispatch(setError(error))
        }
      })
    }

export const deleteSubscription =
  (subscriptionId: string) =>
  (dispatch: typeof store.dispatch) => {
    dispatch(setStatus(StatusE.LOADING))

    apiInstance
      .delete(`/notification/subscription/${subscriptionId}`)
      .then(() => {
        // Refresh subscriptions list
        dispatch(getUserSubscriptions())
      })
      .catch((error: ErrorT | AxiosError) => {
        if (error instanceof AxiosError) {
          const { status, name, message } = error
          dispatch(setError({ status, name, message }))
        } else {
          dispatch(setError(error))
        }
      })
  }
